import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { buildBiofixPlotLines } from 'App/dashboards/_utils/buildBiofixPlotLines'
import { buildCropEventPlotLines } from 'App/dashboards/_utils/buildCropEventPlotLines'
import { legendCheckboxLabelFormatter, syncLegendCheckboxes } from 'App/dashboards/_utils/buildLegendCheckbox'
import { buildPestTooltipFormatter, pestTooltipStyle } from 'App/dashboards/_utils/buildPestTooltip'
import { buildSprayPlotLines } from 'App/dashboards/_utils/buildSprayPlotLines'
import {
  buildSprayWindowBands,
  type TFlightPoint,
  type TSprayWindow,
} from 'App/dashboards/_utils/buildSprayWindowBands'
import { DASHBOARD_FONT_FAMILY } from 'App/dashboards/_utils/dashboardFont'
import type { TPestLayers } from 'App/dashboards/_utils/pestDashboardConfig'
import {
  degreeDaysKeyForInsect,
  phenologyKeyForInsect,
  trapCatchKeyForInsect,
} from 'App/dashboards/_utils/pestValueTypeKeys'
import type { Options, SeriesOptionsType, XAxisPlotLinesOptions } from 'highcharts/highstock'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors, DASHBOARD_DEGREE_DAY_LINE_COLORS } from 'settings/colors'
import { escapeHtml } from 'utils/escapeHtml'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { TBlockInfo } from './getBlocksForProperty'
import type { TWeatherStationInfo } from './getWeatherStationsForProperty'

type TTimeseriesPoint = { timestamp: string; value: number | null }

// Degree days are requested at the points scope: one cumulative-since-biofix series per weather
// station, keyed by the station's lngLat in data.points. Each carries the forecast start and the
// (default) biofix dates in metadata.
type TPointDdMetadata = { forecastStartsAt?: string | null; biofixDates?: string[] }

const getPropertyTimeseries = (
  data: routes.Values.Response | null,
  propertyId: number | null,
  valueKey: string,
): TTimeseriesPoint[] => {
  if (data === null || propertyId === null) return []

  // Type the value-type key (trap catches at property scope), then read by optional chaining — the
  // app's convention rather than widening the whole response container with `as Record<…>`.
  const key =
    valueKey as keyof VV.DomainTypes.TrapCatchesInsect.TValuesReturnWithMetaIgnoringKeying['properties']

  return data.properties?.[propertyId]?.values?.[key]?.[0]?.timeseries ?? []
}

// A weather station's degree day entry (points scope): timeseries + forecast start + biofix dates.
const getPointDdEntry = (
  data: routes.Values.Response | null,
  lngLat: string,
  valueKey: string,
): { timeseries: TTimeseriesPoint[]; metadata: TPointDdMetadata } => {
  if (data === null) return { timeseries: [], metadata: {} }

  const key = valueKey as keyof VV.DomainTypes.DegreeDaysInsect.TValuesReturnWithMetaIgnoringKeying['points']
  const entry = data.points?.[lngLat]?.values?.[key]?.[0]

  return { timeseries: entry?.timeseries ?? [], metadata: entry?.metadata ?? {} }
}

// Phenology has a `series` payload (not `timeseries`) carrying spray windows + modeled flights.
const getPhenologySeries = (
  data: routes.Values.Response | null,
  propertyId: number | null,
  valueKey: string,
): {
  optimalSprayWindows: TSprayWindow[]
  modeledFlightPredictions: TFlightPoint[]
  propertyShouldIncludeSemiosModel: boolean
} => {
  if (data === null || propertyId === null) {
    return { optimalSprayWindows: [], modeledFlightPredictions: [], propertyShouldIncludeSemiosModel: false }
  }

  const key =
    valueKey as keyof VV.DomainTypes.PhenologyInsect.TValuesReturnWithMetaIgnoringKeying['properties']

  const entry = data.properties?.[propertyId]?.values?.[key]?.[0]
  const series = entry?.series

  return {
    optimalSprayWindows: series?.optimalSprayWindows ?? [],
    modeledFlightPredictions: series?.modeledFlightPredictions ?? [],
    propertyShouldIncludeSemiosModel: entry?.metadata?.propertyShouldIncludeSemiosModel ?? false,
  }
}

const toHighchartsPoints = (timeseries: TTimeseriesPoint[]): [number, number | null][] =>
  timeseries.map((d) => [+new Date(d.timestamp), d.value])

const convertDdPoints = (timeseries: TTimeseriesPoint[]): [number, number | null][] =>
  timeseries.map((d) => [+new Date(d.timestamp), unitConverter.insectDegreeDaysCumulative(d.value).value()])

export const buildPestTimingChartOptions = ({
  data,
  propertyId,
  propertyName,
  insectId,
  blocks,
  weatherStations = [],
  layers,
  sprayRecords,
  cultivarNamesById = {},
  customSemiosWindows = [],
  showTrapCatches = false,
  showDegreeDays = false,
}: {
  data: routes.Values.Response | null
  propertyId: number | null
  propertyName?: string
  insectId: number
  // Blocks drive the bloom markers and the spray-record filter.
  blocks: TBlockInfo[]
  // Weather stations drive the degree day series — one line per station (points scope).
  weatherStations?: TWeatherStationInfo[]
  layers: TPestLayers
  sprayRecords: routes.InsectSprayRecords.Response | null
  cultivarNamesById?: Record<number, string>
  // Fixed life-stage Semios windows (cumulative DD) for pests that aren't API-driven (OBLR). Empty
  // for everyone else. See TPestDashboardConfig.customSemiosWindows.
  customSemiosWindows?: { startDD: number; endDD: number }[]
  // Resolved (config AND permission) by the caller — these gate the trap/degree day series.
  showTrapCatches?: boolean
  showDegreeDays?: boolean
}): Options => {
  const trapKey = trapCatchKeyForInsect(insectId)
  const ddKey = degreeDaysKeyForInsect(insectId)
  const trapData = toHighchartsPoints(getPropertyTimeseries(data, propertyId, trapKey))
  const ddConverter = unitConverter.insectDegreeDaysCumulative()
  const ddDecimals = ddConverter.defaultNumberOfDecimalPlaces()
  const ddSuffix = ddConverter.suffix()

  // Trap-catch series only when permitted + the pest has traps (e.g. SJS has none).
  const trapSeries: SeriesOptionsType[] = showTrapCatches
    ? [
        {
          // spline (not line) for the smooth curve in the design; data points are unchanged.
          type: 'spline',
          // Stable id so chart.update(oneToOne) matches it by id (see the ddSeries note below).
          id: 'trap-catches',
          name: translate.phrases.banyanApp('Trap Catches'),
          yAxis: 1,
          data: trapData,
          // Pin the design blue — otherwise Highcharts' auto-palette shifts the color as the
          // number/order of series (degree day lines, etc.) changes.
          color: colors.blue,
          marker: { enabled: true, radius: 3 },
        },
      ]
    : []

  const timezone = moment.tz.guess()

  // One degree day line per weather station (points scope): read each station's cumulative series
  // straight from data.points[lngLat]. Skip stations with no positive values so the legend matches
  // the lines Highcharts actually renders.
  const ddStationGroups = showDegreeDays
    ? weatherStations
        .map((station) => {
          const { timeseries, metadata } = getPointDdEntry(data, station.lngLat, ddKey)

          return { station, timeseries, metadata }
        })
        .filter((group) => group.timeseries.some((p) => (p.value ?? 0) > 0))
    : []

  const ddSeries: SeriesOptionsType[] = ddStationGroups.map((group, i) => ({
    type: 'line',
    // Stable id per station. DashboardChart applies options with chart.update(…, oneToOne: true),
    // which matches series by id — so every series here needs one, or a no-id series gets dropped/
    // misassigned when the series set changes across the incremental updates (sprays vs /values
    // resolve at different times). The legend-only series already carry ids.
    id: `dd-${group.station.lngLat}`,
    name: `${translate.phrases.banyanApp('Degree Days')} - ${group.station.stationName}`,
    yAxis: 0,
    data: convertDdPoints(group.timeseries),
    // One color per station from the design "Four+ Lines" palette (trap-catch blue omitted). Pinned
    // by index so colors are stable per station and don't drift with Highcharts' auto-palette; cycles
    // if a property somehow has more stations than palette entries.
    color: DASHBOARD_DEGREE_DAY_LINE_COLORS[i % DASHBOARD_DEGREE_DAY_LINE_COLORS.length],
    marker: { enabled: false },
    tooltip: { valueDecimals: ddDecimals, valueSuffix: ` ${ddSuffix}` },
  }))

  const blockNamesById = Object.fromEntries(blocks.map((b) => [b.blockId, b.blockName]))

  const { plotLines: sprayPlotLines, legendSeries: spraySeries } = buildSprayPlotLines({
    sprayRecords,
    blockIds: blocks.map((b) => b.blockId),
    blockNamesById,
    timezone,
  })

  const todayPlotLine: XAxisPlotLinesOptions = {
    id: 'today',
    // Align to local midnight (same convention as the daily data points) so the line lands on
    // "today" rather than the current instant, which can drift to the next day's boundary.
    value: +moment.tz(timezone).startOf('day'),
    color: colors.midnight,
    width: 2,
    zIndex: 4,
    label: {
      text: translate.phrases.banyanApp('Today'),
      align: 'center',
      verticalAlign: 'top',
      rotation: 0,
      y: -8,
      style: { color: colors.midnight, fontWeight: 'bold' },
    },
  }

  // Semios + Regional spray-window bands (CM/OFM/OBLR). Phenology is only in `data` when the
  // caller requested it (layers.sprayWindows + permission), so absent data → no bands.
  const phenology = layers.sprayWindows
    ? getPhenologySeries(data, propertyId, phenologyKeyForInsect(insectId))
    : { optimalSprayWindows: [], modeledFlightPredictions: [], propertyShouldIncludeSemiosModel: false }

  // Pests with fixed life-stage windows (OBLR): inject them as semios windows (only on semios-model
  // properties) — the API doesn't return them. They carry modelType 'semios', so the band builder
  // maps each DD threshold → date via the property's semios flights, same as every other band. Their
  // band reads "Semios Management Window"; API-driven pests (CM/OFM) keep the default "Spray Window".
  const synthesizedSemiosWindows: TSprayWindow[] = phenology.propertyShouldIncludeSemiosModel
    ? customSemiosWindows.map((w) => ({
        modelType: 'semios',
        startCumulativeDegreeDays: w.startDD,
        endCumulativeDegreeDays: w.endDD,
      }))
    : []

  const { plotBands: sprayWindowBands, legendSeries: sprayWindowLegendSeries } = buildSprayWindowBands({
    optimalSprayWindows: [...synthesizedSemiosWindows, ...phenology.optimalSprayWindows],
    modeledFlightPredictions: phenology.modeledFlightPredictions,
    timezone,
    // undefined → buildSprayWindowBands' default "Semios Spray Window".
    semiosBandLabel:
      customSemiosWindows.length > 0 ? translate.phrases.banyanApp('Semios Management Window') : undefined,
  })

  // Crop-event markers (Bloom + Hull Split, NOW). Data is only present when requested + permitted.
  const { plotLines: cropEventPlotLines, legendSeries: cropEventLegendSeries } =
    layers.cropEvents.length > 0
      ? buildCropEventPlotLines({
          data,
          propertyId,
          blockIds: blocks.map((b) => b.blockId),
          blockNamesById,
          cultivarNamesById,
          timezone,
        })
      : { plotLines: [], legendSeries: null }

  // Biofix markers from the degree day metadata (biofixDates). Gated with the degree day series,
  // since biofix is degree day data and the dates only arrive when DD was requested/permitted.
  const { plotLines: biofixPlotLines, legendSeries: biofixLegendSeries } = showDegreeDays
    ? buildBiofixPlotLines({
        data,
        stationLngLats: weatherStations.map((s) => s.lngLat),
        ddKey,
        timezone,
      })
    : { plotLines: [], legendSeries: null }

  // Degree day lines run solid up to the forecast start and dotted after. We aggregate multiple
  // stations, so use the LATEST forecast start across them — a single stale station (early forecast
  // start) must not drag the whole dashing back. (The right-panel chart uses the earliest, but it
  // only shows one station at a time.) Note: the shaded "Forecast" background was removed per design.
  const forecastStartsMs = ddStationGroups
    .map((group) =>
      group.metadata.forecastStartsAt ? +moment.tz(group.metadata.forecastStartsAt, timezone) : null,
    )
    .filter((ms) => ms !== null)

  const firstForecastMs = forecastStartsMs.length > 0 ? Math.max(...forecastStartsMs) : null

  // No degree day or trap-catch permission for this pest on this property → show the standard
  // feature-access message (as the existing charts do) instead of the generic "No data to display".
  const noDataText =
    !showTrapCatches && !showDegreeDays && propertyName
      ? translate.phrases.banyanApp('You do not have access to this feature for {{propertyName}}', {
          propertyName: escapeHtml(propertyName),
        })
      : translate.phrases.banyanApp('No data to display')

  return {
    // spacingTop leaves room for the "Today" plot-line label so it isn't clipped at the top.
    // zooming.type 'x' → drag-select a time range to zoom in; a "Reset zoom" button appears.
    chart: {
      height: 350,
      spacingTop: 24,
      zooming: { type: 'x' },
      // Roboto for the chart's SVG text (axis labels, legend) to match the rest of the dashboard.
      style: { fontFamily: DASHBOARD_FONT_FAMILY },
      // Re-sync the legend checkboxes to each series' visibility after every (re)render.
      events: { render: syncLegendCheckboxes },
    },
    title: { text: undefined },
    lang: { noData: noDataText },
    // Hide the "Highcharts.com" watermark in the bottom-right.
    credits: { enabled: false },
    rangeSelector: { enabled: false },
    navigator: { enabled: false },
    scrollbar: { enabled: false },
    legend: {
      enabled: true,
      // We render our own [checkbox][color swatch][name] row in labelFormatter, so hide Highcharts'
      // default series symbol. Checkboxes make the show/hide toggle discoverable.
      useHTML: true,
      symbolWidth: 0,
      symbolHeight: 0,
      symbolPadding: 0,
      squareSymbol: false,
      // lineHeight a touch above the 14px checkbox so the box (and its border) isn't clipped by the
      // legend item's line box.
      itemStyle: { fontSize: '14px', fontWeight: 'normal', lineHeight: '18px' },
      labelFormatter: legendCheckboxLabelFormatter,
    },
    // One shared tooltip for every series, styled like the existing insect trap/degree day charts.
    tooltip: { ...pestTooltipStyle, formatter: buildPestTooltipFormatter({ firstForecastMs, timezone }) },
    // Solid up to the forecast start, dotted beyond (applies to the degree day lines; trap
    // catches have no forecast data so the split is a no-op for them). Always keep plotOptions.line
    // defined — passing `undefined` breaks Highcharts' option merge on update.
    plotOptions: {
      line: {
        zoneAxis: 'x',
        zones: firstForecastMs ? [{ value: firstForecastMs }, { dashStyle: 'Dot' }] : undefined,
      },
    },
    xAxis: {
      type: 'datetime',
      // highstock defaults to an ordinal axis, which collapses date gaps based on the union of all
      // series' points — so series that end early (most stations) distort positioning and break
      // shared-tooltip hit-testing past their last point. A real datetime axis fixes both.
      ordinal: false,
      crosshair: { snap: true, zIndex: 3 },
      plotLines: [...sprayPlotLines, ...cropEventPlotLines, ...biofixPlotLines, todayPlotLine],
      plotBands: [...sprayWindowBands],
    },
    yAxis: [
      {
        // No unit in the axis title — matches the existing Hub trap+degree-days chart.
        title: { text: translate.phrases.banyanApp('Degree Days') },
        opposite: false,
        allowDecimals: true,
      },
      {
        title: { text: translate.phrases.banyanApp('Trap Catches') },
        opposite: true,
        allowDecimals: false,
      },
    ],
    series: [
      ...trapSeries,
      ...ddSeries,
      ...(spraySeries ? [spraySeries] : []),
      ...sprayWindowLegendSeries,
      ...(cropEventLegendSeries ? [cropEventLegendSeries] : []),
      ...(biofixLegendSeries ? [biofixLegendSeries] : []),
    ],
  }
}
